// Globaler 403-Handler für feinkörniges Feature-Gating: Wenn ein /api-Aufruf mit
// 403 + Code `feature_not_included` abgelehnt wird (z. B. ein Professional-Web-
// Nutzer löst eine Advanced-only-Aktion wie PDF-Export, Fluglog-Auswertung oder
// Dokumentenverwaltung aus), soll die App einen freundlichen Upgrade-Hinweis
// zeigen — statt einer rohen Fehlermeldung oder eines stillen Fehlers.
//
// Analog zu subscriptionLock.ts: Der Query-Client lebt außerhalb des React-
// Routers und kann nicht selbst navigieren/rendern. Stattdessen meldet er den
// Sperr-Fall (inkl. betroffenem Feature-Key) über dieses kleine Pub/Sub an eine
// im Router montierte Komponente (FeatureLockNotice), die den Hinweis anzeigt.

import { getFeatureDefinition } from "../../../shared/featureCatalog";

/** Server-Code für „Feature nicht im Tarif enthalten" (siehe requireFeature). */
export const FEATURE_NOT_INCLUDED_CODE = "feature_not_included";

type Listener = (featureKey: string) => void;

const listeners = new Set<Listener>();
let lastFired = 0;
let lastFeature: string | null = null;

/** Registriert einen Listener; gibt eine Cleanup-Funktion zurück. */
export function onFeatureNotIncluded(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Meldet, dass ein API-Aufruf wegen eines nicht enthaltenen Features (403)
 * blockiert wurde. Entprellt pro Feature, damit gleichzeitige Hintergrund-
 * Queries nicht mehrfach feuern.
 */
export function notifyFeatureNotIncluded(featureKey: string): void {
  const now = Date.now();
  if (lastFeature === featureKey && now - lastFired < 1500) return;
  lastFired = now;
  lastFeature = featureKey;
  for (const fn of listeners) fn(featureKey);
}

/** Anzeigename eines Features (aus dem geteilten Katalog) mit Fallback. */
export function featureDisplayName(featureKey: string): string {
  return getFeatureDefinition(featureKey)?.name ?? "Diese Funktion";
}

/** Beschreibung eines Features (aus dem geteilten Katalog) mit Fallback. */
export function featureDescription(featureKey: string): string {
  return (
    getFeatureDefinition(featureKey)?.description ??
    "Diese Funktion ist in Ihrem aktuellen Paket nicht enthalten."
  );
}
